/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  SubmitEvent,
  useState
} from "react";

import { Trans, useTranslation } from "react-i18next";

import { QRCodeSVG } from "qrcode.react";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import TextLink from "../../../../../components/TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../../../components/TextLink/types";
import { useAuth } from "../../../../../context/Auth/AuthProvider";
import { OtpInputField } from "../../../../../shared/auth/OtpInputField";
import useTwoFaContent from "../../../hooks/useTwoFaContent";
import {
  AUTHENTICATION_TYPES,
  ENABLE_AUTHENTICATION_STEPS
} from "../../../types";
import { BorderFrame } from "../components/BorderFrame";
import { Buttons } from "../components/Buttons";
import { InnerBottom } from "../components/InnerBottom";
import { InnerTop } from "../components/InnerTop";
import { useStyles } from "../styles";

export interface SendOtpCodeProps {
  secretKey: string;
  error: string | null;
  isLoading: boolean;
  changeCode: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  otpCode: string;
  authType: AUTHENTICATION_TYPES | null;
  changeStep: (newStep: ENABLE_AUTHENTICATION_STEPS) => void;
  setOtpCode: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string | null>>;
  sendOtpCodeSubmit: () => void;
}

export const SendOtpCode = ({
  secretKey,
  error,
  isLoading,
  changeCode,
  onBlur,
  otpCode,
  authType,
  changeStep,
  setOtpCode,
  setError,
  sendOtpCodeSubmit
}: SendOtpCodeProps) => {
  const { user } = useAuth();
  const { otpTopTitleComponents } = useTwoFaContent();
  const { t } = useTranslation();

  const [isQr, setIsQr] = useState(true);

  const { authenticatorQrBlock, otpContentContainer } = useStyles();

  const toggleIsQr = () => setIsQr(!isQr);

  const handleSecondaryButtonClick = () => {
    changeStep(ENABLE_AUTHENTICATION_STEPS.SelectMethod);
    setOtpCode("");
    setError(null);
  };

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendOtpCodeSubmit?.();
  };

  return (
    <form onSubmit={handleSubmit} data-testid="send-otp-code-form">
      <BorderFrame>
        <InnerTop
          title={
            <Trans
              i18nKey="Security##2FA##send otp code##title"
              components={otpTopTitleComponents}
            />
          }
        />
        <InnerBottom>
          <div css={css(otpContentContainer)}>
            <Typography variant="body2Regular">
              {isQr
                ? t("Password##scan QR code")
                : t("Password##type text code")}
              :
            </Typography>

            <div css={css(authenticatorQrBlock)}>
              {isQr ? (
                <QRCodeSVG
                  data-testid="qr-code"
                  size={144}
                  fgColor={colors.textDark}
                  value={`otpauth://totp/${import.meta.env.VITE_2FA_NAME}:${
                    user?.email
                  }?secret=${secretKey}&issuer=${
                    import.meta.env.VITE_2FA_NAME
                  }`}
                />
              ) : (
                <Typography variant="body1SemiBold">{secretKey}</Typography>
              )}

              <TextLink
                size={TEXT_LINK_SIZES.Normal}
                onClick={toggleIsQr}
                css={css({
                  margin: "16px 0 24px 0"
                })}
              >
                {isQr
                  ? t("Password##click to type code")
                  : t("Password##click to scan code")}
                .
              </TextLink>
            </div>

            <OtpInputField
              value={otpCode}
              isLoading={isLoading}
              error={error}
              onChange={changeCode}
              isRequired
              onBlur={onBlur}
            />

            <Buttons
              isLoadingPrimary={isLoading}
              isPrimaryBtnDisabled={
                otpCode.length < 6 || authType === null || Boolean(error)
              }
              primaryBtnTitle={t("Button##verify")}
              secondaryBtnTitle={t("Button##back")}
              onSecondaryBtnClick={handleSecondaryButtonClick}
            />
          </div>
        </InnerBottom>
      </BorderFrame>
    </form>
  );
};
